// src/ApiPanel/context/ApiPanelSessionContext.jsx
// Session provider for the API Panel, wired to the real Mokshiq auth API.
//
// Identity always comes from the server (`GET /auth/me`) and is never trusted
// from local storage. That matters more than it looks: the login response
// carries the user's role, and if the panel cached that role and used it to
// decide what to show, anyone could edit localStorage and hand themselves an
// "Admin" UI. Here, a tampered token buys nothing — the server re-derives who
// you are on every page load, and every report endpoint re-checks your role
// server-side anyway. What the client knows is only ever used to decide what
// to *render*, never what is *allowed*.
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiPanelFetch } from "../utils/apiPanelFetch";
import {
  API_PANEL_ENDPOINTS,
  getApiPanelToken,
  setApiPanelToken,
  clearApiPanelToken,
} from "../constants/apiPanelAuth";

const ApiPanelSessionContext = createContext(null);

const ANON = { status: "anon", user: null };

export function ApiPanelSessionProvider({ children }) {
  const [session, setSession] = useState({ ...ANON, status: "loading" });

  const load = useCallback(async () => {
    if (!getApiPanelToken()) {
      setSession({ ...ANON });
      return;
    }
    try {
      const res = await apiPanelFetch(API_PANEL_ENDPOINTS.ME);
      const user = res?.data || res;
      if (!user?.id) throw new Error("no identity returned");
      setSession({ status: "authed", user });
    } catch {
      clearApiPanelToken();
      setSession({ ...ANON });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Step 1 of 2 — validate phone + password server-side and trigger the OTP.
   * Returns the raw response so the login page can show the delivery channel.
   */
  // `platform` is not sent: the backend reads it from the X-Platform header
  // via detect_platform(), not from the body, and defaults to 'web'.
  const requestOtp = useCallback(async ({ phone, password }) => {
    return apiPanelFetch(API_PANEL_ENDPOINTS.LOGIN_REQUEST, {
      method: "POST",
      form: { phone, password },
    });
  }, []);

  /**
   * Step 2 of 2 — exchange the OTP for a JWT.
   *
   * The password is sent again because the backend re-authenticates on this
   * call for web logins; the OTP alone is not treated as proof of identity.
   */
  const verifyOtp = useCallback(
    async ({ phone, password, otp }) => {
      const res = await apiPanelFetch(API_PANEL_ENDPOINTS.LOGIN_WITH_OTP, {
        method: "POST",
        form: { phone, password, otp },
      });
      const token = res?.token || res?.data?.token;
      if (!token) throw new Error(res?.message || "Login failed");
      setApiPanelToken(token);
      await load();
      return res;
    },
    [load]
  );

  /**
   * Ends this session only. `scope: "all"` revokes every session the user has
   * on every device — offered in the UI as "sign out everywhere" for the case
   * where someone thinks their account is compromised.
   */
  const logout = useCallback(async (scope = "this") => {
    const endpoint =
      scope === "all"
        ? API_PANEL_ENDPOINTS.LOGOUT_ALL
        : API_PANEL_ENDPOINTS.LOGOUT_THIS_DEVICE;
    try {
      await apiPanelFetch(endpoint, { method: "POST", form: {} });
    } catch {
      // Clear locally regardless. If the revoke call failed the token may
      // still be live server-side until it expires, so this is a best-effort
      // logout, not a guarantee — which is exactly why the backend keeps
      // ActiveSession rows and can revoke centrally.
    } finally {
      clearApiPanelToken();
      setSession({ ...ANON });
    }
  }, []);

  return (
    <ApiPanelSessionContext.Provider
      value={{ ...session, requestOtp, verifyOtp, logout, refresh: load }}
    >
      {children}
    </ApiPanelSessionContext.Provider>
  );
}

export function useApiPanelSession() {
  const ctx = useContext(ApiPanelSessionContext);
  if (!ctx) {
    throw new Error("useApiPanelSession must be used within an ApiPanelSessionProvider");
  }
  return ctx;
}
